/**
 * Password Validation
 *
 * Single source of truth for password complexity rules.
 * Exports a Zod schema (for form/request validation) and an imperative
 * `validatePassword()` helper (for API route error arrays).
 *
 * Default schema messages are next-intl keys under
 * `myAccount.editPassword.requirements` — resolve with `useTranslations` /
 * `getTranslations` on the client, or pass already-translated strings into
 * {@link createPasswordSchema} / {@link validatePassword}.
 *
 * Consumers:
 * - lib/forms/schemas/auth.ts (client-side react-hook-form)
 * - lib/auth/schemas.ts (BFF request validation / OpenAPI)
 * - API routes (imperative validatePassword)
 */

import { z } from 'zod';

import type { ApiError } from '@/lib/api/responses';

/** Minimum password length requirement */
export const PASSWORD_MIN_LENGTH = 10;

/** Password validation requirements flags used by UI checklists */
export const PASSWORD_REQUIREMENTS = {
  minLength: PASSWORD_MIN_LENGTH,
  uppercase: true,
  lowercase: true,
  number: true,
  specialChar: true,
} as const;

/** next-intl namespace for {@link PASSWORD_MESSAGE_KEYS} */
export const PASSWORD_REQUIREMENTS_NAMESPACE = 'myAccount.editPassword.requirements';

/**
 * Leaf keys under {@link PASSWORD_REQUIREMENTS_NAMESPACE}.
 * Note: schema field `specialChar` maps to translation key `special`.
 */
export type PasswordRequirementsKey =
  | 'minLength'
  | 'uppercase'
  | 'lowercase'
  | 'number'
  | 'special';

/**
 * Messages (translated strings or {@link PasswordRequirementsKey} values)
 * plugged into {@link createPasswordSchema}.
 */
export interface PasswordSchemaMessages {
  /** Failed when shorter than {@link PASSWORD_MIN_LENGTH} */
  minLength: string;
  /** Failed when missing an uppercase letter */
  uppercase: string;
  /** Failed when missing a lowercase letter */
  lowercase: string;
  /** Failed when missing a digit */
  number: string;
  /** Failed when missing a special character */
  specialChar: string;
}

/**
 * Default schema / API error message values.
 * Keys under {@link PASSWORD_REQUIREMENTS_NAMESPACE} (`specialChar` → `special`).
 */
export const PASSWORD_MESSAGE_KEYS: PasswordSchemaMessages = {
  minLength: 'minLength',
  uppercase: 'uppercase',
  lowercase: 'lowercase',
  number: 'number',
  specialChar: 'special',
};

/**
 * Type guard: whether `message` is a password-requirements translation key.
 *
 * @param message - Candidate string from a Zod/API error
 */
export function isPasswordRequirementsKey(message: string): message is PasswordRequirementsKey {
  return (Object.values(PASSWORD_MESSAGE_KEYS) as string[]).includes(message);
}

/**
 * Resolve an API/schema password message that may be a requirements key.
 *
 * @param message - Raw error message (key or already-translated copy)
 * @param t - Translator bound to {@link PASSWORD_REQUIREMENTS_NAMESPACE}
 * @returns Translated string when `message` is a known key; otherwise `message` unchanged
 *
 * @example
 * resolvePasswordRequirementsMessage('minLength', tPasswordRules)
 * // → "Mindestens 10 Zeichen"
 */
export function resolvePasswordRequirementsMessage(
  message: string,
  t: (key: PasswordRequirementsKey) => string
): string {
  return isPasswordRequirementsKey(message) ? t(message) : message;
}

/**
 * Build translated password schema messages from a requirements-namespace translator.
 *
 * @param t - Translator bound to {@link PASSWORD_REQUIREMENTS_NAMESPACE}
 * @returns Messages suitable for {@link createPasswordSchema}
 *
 * @example
 * const t = useTranslations('myAccount.editPassword.requirements');
 * createPasswordSchema(getPasswordSchemaMessages(t));
 */
export function getPasswordSchemaMessages(
  t: (key: PasswordRequirementsKey) => string
): PasswordSchemaMessages {
  return {
    minLength: t('minLength'),
    uppercase: t('uppercase'),
    lowercase: t('lowercase'),
    number: t('number'),
    specialChar: t('special'),
  };
}

/**
 * Password complexity Zod schema with caller-supplied i18n messages (or keys).
 *
 * @param messages - Per-rule error messages ({@link PasswordSchemaMessages})
 * @returns Zod string schema enforcing length + character-class rules
 */
export function createPasswordSchema(messages: PasswordSchemaMessages) {
  return z
    .string()
    .min(PASSWORD_MIN_LENGTH, messages.minLength)
    .regex(/[A-Z]/, messages.uppercase)
    .regex(/[a-z]/, messages.lowercase)
    .regex(/\d/, messages.number)
    .regex(/[ !"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]/, messages.specialChar);
}

/**
 * Canonical Zod schema for password complexity.
 * Messages are {@link PASSWORD_MESSAGE_KEYS} — translate before showing to users.
 */
export const passwordSchema = createPasswordSchema(PASSWORD_MESSAGE_KEYS);

/**
 * Validate password against all requirements.
 * Returns an array of errors (empty if valid).
 *
 * Default messages are translation keys ({@link PASSWORD_MESSAGE_KEYS}).
 * Pass already-translated strings when the caller has locale context.
 *
 * @param password - The password to validate
 * @param fieldName - The field name to use in error objects (default: `'password'`)
 * @param messages - Schema messages (keys or translated strings)
 * @returns Empty array when valid; otherwise {@link ApiError} entries per failing rule
 *
 * @example
 * validatePassword('short')
 * // → [{ field: 'password', message: 'minLength', code: 'PASSWORD_TOO_SMALL' }, ...]
 */
export function validatePassword(
  password: string,
  fieldName: string = 'password',
  messages: PasswordSchemaMessages = PASSWORD_MESSAGE_KEYS
): ApiError[] {
  const result = createPasswordSchema(messages).safeParse(password);
  if (result.success) return [];

  return result.error.issues.map((issue) => ({
    field: fieldName,
    message: issue.message,
    code: `PASSWORD_${issue.code.toUpperCase()}`,
  }));
}

/**
 * Check if a password meets all requirements.
 *
 * @param password - Candidate password
 * @returns `true` when the password satisfies {@link passwordSchema}
 */
export function isPasswordValid(password: string): boolean {
  return passwordSchema.safeParse(password).success;
}
